import {
  computeDistanceBetween, computeOffset,
} from 'spherical-geometry-js';
import { createColor } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type {
  LatLng, LatLngBounds,
} from '~/_shared/types/latLng';
import {
  type DriveTimePolygonProximity, type GroupRadiusProximity, type IndividualRadiusProximity, isDriveTimePolygon,
} from '~/_shared/types/proximity/proximity.types';
import {
  convertColorToWebGLColor, getContrastColor,
} from '~/_shared/utils/colors/colors.helpers';
import { convertUnitToMeters } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import {
  createWebglOverlayLabelWithBody, type WebglOverlayLabelWithBody,
} from '~/_shared/utils/webgl/labelWithBody';
import type {
  MapObjectLabelCalloutConfig, MapObjectLabelConfig,
} from '~/map/map/mapObjects/mapObject.types';
import type { ProximityZIndex } from '~/map/map/proximity/mapProximityManager';
import { BoundingBox } from '../boundingBox';

const labelPaddingHorizontal = 5;
const labelPaddingVertical = 1;

const createProximityDistanceLabelMapObjectConfig = (
  proximity: DriveTimePolygonProximity | IndividualRadiusProximity | GroupRadiusProximity,
  labelBounds: LatLngBounds,
): MapObjectLabelConfig => {
  return {
    boundaries: getProximityLabelBoundaries(labelBounds),
    color: textFillColor(proximity.styles.color),
    opacity: 1,
    horizontalAnchor: 'center',
    interactive: false,
    offsetX: 0,
    offsetY: -1, // move 1 pixel higher so it covers top edge of circle area
    padding: {
      b: labelPaddingVertical,
      l: labelPaddingHorizontal,
      r: labelPaddingHorizontal,
      t: labelPaddingVertical,
    },
    text: createDistanceLabelText(proximity),
    verticalAnchor: 'top',
    staticSize: true,
  };
};

const createProximityCalloutMapObjectConfig = (
  proximity: DriveTimePolygonProximity | IndividualRadiusProximity | GroupRadiusProximity,
  offsetX?: number,
  offsetY?: number,
): MapObjectLabelCalloutConfig => ({
  offset: { x: offsetX ?? 0, y: offsetY ?? 0 },
  fillColor: convertColorToWebGLColor(proximity.styles.color, 1),
  interactive: false,
  triangle: false,
  borderRadius: 5,
  borderWidth: 0,
  staticSize: true,
});

export const createDistanceLabelEntity = (
  proximity: DriveTimePolygonProximity | IndividualRadiusProximity | GroupRadiusProximity,
  labelBounds: LatLngBounds,
  zIndex: ProximityZIndex,
  isHidden: boolean,
): WebglOverlayLabelWithBody | null => {
  if (isHidden) {
    return null;
  }

  const labelMapObjectConfig = createProximityDistanceLabelMapObjectConfig(proximity, labelBounds);
  const { text, color, offsetY, offsetX, ...restOfTheConfig } = labelMapObjectConfig;

  return createWebglOverlayLabelWithBody({
    ...restOfTheConfig,
    ...createProximityCalloutMapObjectConfig(proximity, offsetX, offsetY),
    text: {
      fillColor: color,
      value: text,
      linePadding: 0,
    },
  }, { background: zIndex.label, text: zIndex.labelText });
};

export const getCircleLabelBoundingBox = (circleLatLng: LatLng, radius: number, unit: UnitSystem): LatLngBounds => {
  const radiusInMeters = convertUnitToMeters(radius, unit);
  const labelDimensionInMeters = radiusInMeters / 3;
  const northPoint = computeOffset(circleLatLng, radiusInMeters, 0);
  const southPoint = computeOffset(circleLatLng, radiusInMeters - labelDimensionInMeters, 0);
  const northEastPoint = computeOffset(northPoint, labelDimensionInMeters, 90);
  const southWestPoint = computeOffset(southPoint, labelDimensionInMeters, 270);

  return {
    ne: { lat: northEastPoint.lat(), lng: northEastPoint.lng() },
    sw: { lat: southWestPoint.lat(), lng: southWestPoint.lng() },
  };
};

export const getPolygonLabelBounds = (proximity: DriveTimePolygonProximity): {
  bounds: LatLngBounds | null;
  fallback: LatLngBounds;
} => {
  let pathsBounds = null;
  if (proximity.data.paths && proximity.data.paths[0]?.path[0]) {
    let middleTopPoint = proximity.data.paths[0].path[0];
    const pathsBB = proximity.data.paths.reduce((bb, polygon) => {
      polygon.path.forEach(point => {
        if (point.lat > middleTopPoint.lat) {
          middleTopPoint = point;
        }
        bb.extend(point);
      });
      return bb;
    }, new BoundingBox());

    const northWestPoint = { lat: pathsBB.getNorthEast().lat, lng: pathsBB.getSouthWest().lng };
    const polygonWidthInMeters = computeDistanceBetween(
      pathsBB.getNorthEast(),
      northWestPoint,
    );
    const polygonHeightInMeters = computeDistanceBetween(
      pathsBB.getNorthEast(),
      { lat: pathsBB.getSouthWest().lat, lng: pathsBB.getNorthEast().lng }
    );
    const labelDimensionInMeters = (polygonWidthInMeters > polygonHeightInMeters
      ? polygonWidthInMeters : polygonHeightInMeters) / 2;
    const labelNorthEastPoint = computeOffset(middleTopPoint, labelDimensionInMeters / 2, 90);
    const labelNorthWestPoint = computeOffset(middleTopPoint, labelDimensionInMeters / 2, 270);
    const labelSouthWestPoint = computeOffset(labelNorthWestPoint, labelDimensionInMeters, 180);

    pathsBounds = {
      ne: { lat: labelNorthEastPoint.lat(), lng: labelNorthEastPoint.lng() },
      sw: { lat: labelSouthWestPoint.lat(), lng: labelSouthWestPoint.lng() },
    };
  }
  return {
    bounds: pathsBounds,
    fallback: {
      ne: { lat: proximity.data.lat, lng: proximity.data.lng },
      sw: { lat: proximity.data.lat, lng: proximity.data.lng },
    },
  };
};

export const createDistanceLabelText = (proximity: DriveTimePolygonProximity | IndividualRadiusProximity | GroupRadiusProximity) => {
  if (isDriveTimePolygon(proximity)) {
    return `${proximity.data.hours}H ${proximity.data.minutes}M`;
  }
  else {
    return `${proximity.data.radius}${proximity.data.unit === UnitSystem.metric ? 'KM' : 'MI'}`;
  }
};

const textFillColor = (proximityFillColor: string) =>
  convertColorToWebGLColor(getContrastColor(createColor('#fff'), createColor(proximityFillColor)).hex, 1);

export const createProximityDistanceLabelWithCalloutVisuals = (
  proximity: IndividualRadiusProximity | GroupRadiusProximity,
  labelBounds: LatLngBounds,
): Readonly<{ label: MapObjectLabelConfig; callout: MapObjectLabelCalloutConfig }> => {
  const mapObjectLabelConfig = createProximityDistanceLabelMapObjectConfig(proximity, labelBounds);

  return {
    label: mapObjectLabelConfig,
    callout: createProximityCalloutMapObjectConfig(proximity),
  };
};

export const getProximityLabelBoundaries = ({ ne, sw }: Readonly<LatLngBounds>) => {
  return {
    enabled: true,
    minFontSize: 5,
    maxFontSize: 20,
    sw,
    ne,
  };
};
